import { einheitLabel } from "../../lib/labels";
import { eur, MONAT_KURZ } from "../../hooks/useVertraege";
import type { VertragDetail } from "../../hooks/useVertraege";
import { einsaetze } from "../../lib/frequenz";
import { zahl } from "../../lib/zahl";
import { monatsListe, titel, Zeile } from "./teile";

// ── Tab: Leistungen ─────────────────────────────────────────
export function TabLeistungen({ v }: { v: VertragDetail }) {
  if (v.leistungen.length === 0) return <div className="pf-empty">Keine Leistungen.</div>;
  return (
    <>
      {v.leistungen.map(l => {
        const eff = Number(l.einzelpreis_netto) * Number(v.faktor);
        const anzahl = einsaetze(l);
        const jahr = eff * Number(l.menge) * anzahl;
        return (
          <div key={l.id} className="pf-row" style={{ cursor: "default", opacity: l.optional && !l.beauftragt ? 0.55 : 1 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{l.optional ? "◇ " : ""}{titel(l)}</div>
              <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 2 }}>
                {zahl(l.menge)} {einheitLabel(l.einheit)} · {anzahl}×/Jahr
                {/* „Mai Mai Sep" wäre im Kalender richtig und in einer Zeile
                    falsch — zwei Einsätze im Mai stehen als „Mai (2×)" da. */}
                {anzahl > 0 ? ` · ${monatsListe(l.monate)}` : ""}
              </div>
            </div>
            <div style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{eur(jahr)}/Jahr</div>
          </div>
        );
      })}
    </>
  );
}

// ── Tab: Jahresplan (12-Monats-Matrix) ──────────────────────
export function TabJahresplan({ v }: { v: VertragDetail }) {
  const plan: Record<number, string[]> = {};
  for (const l of v.leistungen) {
    if (l.optional && !l.beauftragt) continue;
    // MIT Mehrfachnennung: zwei Einsätze im März sind zwei Zeilen im März.
    for (const m of l.monate) (plan[m] ??= []).push(titel(l));
  }
  return (
    <div className="pf-card">
      {MONAT_KURZ.map((label, i) => {
        const e = plan[i + 1] || [];
        return (
          <div key={label} style={{ display: "flex", gap: 12, padding: "7px 0", borderBottom: i < 11 ? "1px solid var(--pf-line)" : "none" }}>
            <div style={{ width: 40, fontWeight: 800, fontSize: 13, color: e.length ? "var(--pf-accent)" : "var(--pf-ink-3)" }}>{label}</div>
            <div style={{ flex: 1, fontSize: 13.5, color: e.length ? "var(--pf-ink)" : "var(--pf-ink-3)" }}>
              {e.length ? e.join(", ") : "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tab: Wirtschaftlichkeit (Plan + Ist) ────────────────────
export function TabWirtschaft({ v }: { v: VertragDetail }) {
  const k = v.kalkulation;
  return (
    <>
      <div className="pf-seclabel" style={{ marginTop: 0 }}>Plan</div>
      <div className="pf-card">
        <Zeile l="Deckungsbeitrag / Jahr" r={eur(k.deckungsbeitrag)} strong />
        <Zeile l="DB je Stunde" r={k.db_pro_stunde ? eur(k.db_pro_stunde) : "—"} />
        <Zeile l="Geplante Stunden / Jahr" r={`${k.jahres_zeit_stunden} h`} />
        <Zeile l="Kosten / Jahr" r={eur(k.jahres_kosten)} />
        <Zeile l="Lohnanteil § 35a" r={eur(k.lohnanteil_netto_35a)} />
      </div>
      {v.ist ? (
        <>
          <div className="pf-seclabel">Ist</div>
          <div className="pf-card">
            <Zeile l="Einsätze" r={`${v.ist.einsaetze_erledigt} / ${v.ist.einsaetze_gesamt}`} />
            <Zeile l="Ist-Stunden" r={`${v.ist.stunden} h (Plan ${k.jahres_zeit_stunden} h)`} />
            <Zeile l="Lohnkosten" r={eur(v.ist.lohnkosten)} />
            <Zeile l="Material / Ausgaben" r={eur(v.ist.ausgaben)} />
            <Zeile l="Gewinn bisher" r={eur(v.ist.gewinn_bisher)} strong />
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 10 }}>
          Ist-Zahlen erscheinen, sobald der Vertrag aktiv ist und Einsätze laufen.
        </div>
      )}
    </>
  );
}

